import { useState, useEffect } from 'react';
import {
  Card,
  Text,
  Button,
  Input,
  Spinner,
  Badge,
} from '@fluentui/react-components';
import {
  BuildingRegular,
  CheckmarkCircleRegular,
  ChatRegular,
  CodeRegular,
  StorageRegular,
  MoneyRegular,
} from '@fluentui/react-icons';
import { TenantService } from '@/services/tenantService';
import { Tenant, DashboardMetrics } from '@/types';
import { useAuth } from '@/auth/AuthContext';
import InviteAdminModal from '@/components/InviteAdminModal';
import './Dashboard.css';

interface MetricCardProps {
  icon: React.ReactNode;
  value: string | number;
  label: string;
  color?: string;
}

function MetricCard({ icon, value, label, color = '--brand-primary' }: MetricCardProps) {
  return (
    <Card className="metric-card">
      <div className="metric-icon" style={{ color: `var(${color})` }}>
        {icon}
      </div>
      <Text size={500} weight="bold" className="metric-value">
        {value}
      </Text>
      <Text size={200} className="metric-label">
        {label}
      </Text>
    </Card>
  );
}

export default function Dashboard() {
  const { user, token } = useAuth();
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      if (!token) return;

      try {
        setLoading(true);
        const [metricsData, tenantsData] = await Promise.all([
          TenantService.getDashboardMetrics(token),
          TenantService.getAllTenants(token),
        ]);
        setMetrics(metricsData);
        setTenants(Array.isArray(tenantsData) ? tenantsData : []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [token]);

  const filteredTenants = tenants.filter(
    (tenant) =>
      tenant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tenant.domain.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="dashboard-loading">
        <Spinner size="extra-large" />
        <Text>Loading dashboard...</Text>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div>
          <h1>Dashboard</h1>
          <Text size={300}>
            Welcome back, {user?.name}! Manage all Sherpai tenants below.
          </Text>
        </div>
        {user?.role === 'owner' && (
          <Button
            appearance="primary"
            onClick={() => setShowInviteModal(true)}
          >
            + Invite Admin
          </Button>
        )}
      </div>

      {error && <div className="error-banner">{error}</div>}

      {metrics && (
        <div className="metrics-grid">
          <MetricCard
            icon={<BuildingRegular />}
            value={metrics.totalTenants}
            label="Total Tenants"
          />
          <MetricCard
            icon={<CheckmarkCircleRegular />}
            value={metrics.activeTenants}
            label="Active Tenants"
            color="--color-success"
          />
          <MetricCard
            icon={<ChatRegular />}
            value={metrics.totalMessages.toLocaleString()}
            label="Total Messages"
            color="--color-info"
          />
          <MetricCard
            icon={<CodeRegular />}
            value={metrics.totalApiCalls.toLocaleString()}
            label="API Calls"
            color="--color-info"
          />
          <MetricCard
            icon={<StorageRegular />}
            value={`${metrics.totalStorageUsedGb.toFixed(2)}GB`}
            label="Storage Used"
            color="--color-warning"
          />
          <MetricCard
            icon={<MoneyRegular />}
            value={`$${metrics.totalRevenue.toLocaleString()}`}
            label="Total Revenue"
            color="--color-success"
          />
        </div>
      )}

      <Card className="tenants-section">
        <div className="section-header">
          <h2>Tenants</h2>
          <Input
            placeholder="Search by name or domain..."
            value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
            style={{ width: '300px' }}
          />
        </div>

        <div className="table-container">
          <table className="tenants-table">
            <thead>
              <tr>
                <th>Tenant Name</th>
                <th>Domain</th>
                <th>Status</th>
                <th>Tier</th>
                <th>Country</th>
                <th>Industry</th>
              </tr>
            </thead>
            <tbody>
              {filteredTenants.map((tenant) => (
                <tr key={tenant.id}>
                  <td><Text weight="semibold">{tenant.name}</Text></td>
                  <td><Text>{tenant.domain}</Text></td>
                  <td>
                    <Badge 
                      appearance="outline" 
                      color={tenant.status === 'active' ? 'success' : tenant.status === 'suspended' ? 'danger' : 'warning'}
                    >
                      {tenant.status}
                    </Badge>
                  </td>
                  <td><Text>{(tenant.subscriptionTier || '').toUpperCase()}</Text></td>
                  <td><Text>{tenant.orgCountry || '—'}</Text></td>
                  <td><Text>{tenant.orgIndustry || '—'}</Text></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {showInviteModal && (
        <InviteAdminModal
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
        />
      )}
    </div>
  );
}
